import { getErrorMessage } from '@fruitful/fruitils';
import { router, useLocalSearchParams } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { Keyboard, Pressable } from 'react-native';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BodyMediumRegular,
    DEFAULT_COLOR,
    DEFAULT_ICON,
    FullScreenSpinner,
    isWeb,
    SafeAreaView,
    ScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import { useUserAuthorization } from '../../../../auth/hooks/useUserAuthorization';
import { isEmpty } from '../../../../common/misc';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { canUserCreateBankAccount } from '../../../../common/utils/bankingUtils';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../../logger/Logger';
import useCreateSpace from '../../../hooks/useCreateSpace';
import useGetAllBankAccountDetailsLegacy from '../../../hooks/useGetAllBankAccountDetailsLegacy';
import useGetJointAccountHolders from '../../../hooks/useGetJointAccountHolders';
import useSendJointAccountHolderInvite from '../../../hooks/useSendJointAccountHolderInvite';
import useUpdateBankAccount from '../../../hooks/useUpdateBankAccount';
import { editingSpaceAtom } from '../../../state/SpacesAtoms';
import { preselectedTransferAccountsAtom } from '../../../state/TransferAtoms';
import type { UpdateBankAccountInfo } from '../../../types/BankAccount';
import { AccountType } from '../../../types/BankAccount';
import {
    getBankingDisclosures,
    HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
} from '../../Disclosures';
import Color from './steps/Color';
import Icon from './steps/Icon';
import Name from './steps/Name';
import Ownership from './steps/Ownership';

enum CreateEditSteps {
    NAME = 'name',
    ICON = 'icon',
    COLOR = 'color',
    OWNERSHIP = 'ownership',
}

const CreateEditSpace = () => {
    LogRocketFacade.tagPage('CreateEditPageLegacy');

    const user = useAuthedUser();
    const { accessToRestrictedCapabilities } = useUserAuthorization();
    const toast = useToastController();

    const { fromKyc } = useLocalSearchParams();

    const setPreselectedTransferAccount = useSetAtom(
        preselectedTransferAccountsAtom,
    );
    const [account, setAccount] = useAtom(editingSpaceAtom);

    // If an account is passed through, then we're editing it rather than creating a new one
    const isEditMode = account != null;
    const isCreateMode = !isEditMode;

    // If editing an individual account or creating a new account, allow joint user to be invited
    const allowJointInvite = account?.jointUser == null;

    const hideDisclosures = isEditMode || fromKyc === 'true';

    const [index, setIndex] = useState(0);

    const [updateAccountInfo, setUpdateAccountInfo] =
        useState<UpdateBankAccountInfo>({
            nickname: account?.nickname ?? undefined,
            icon: account?.icon ?? DEFAULT_ICON,
            color: account?.color ?? DEFAULT_COLOR,
        });

    const { data: jointAccountHolders } = useGetJointAccountHolders({
        userId: user.id,
    });

    const { refetchAccountDetails } = useGetAllBankAccountDetailsLegacy();

    const {
        mutateAsync: createSavingsSpace,
        isPending: isCreateSavingsSpacePending,
    } = useCreateSpace();
    const { mutateAsync: updateAccount, isPending: isUpdateAccountPending } =
        useUpdateBankAccount();
    const {
        mutateAsync: sendJointInvite,
        isPending: isSendJointInvitePending,
    } = useSendJointAccountHolderInvite();

    const routes = Object.values(CreateEditSteps).map((step) => ({
        key: step,
    }));

    // Combine partner member and joint account holder
    const associatedUsers = uniqBy(
        [user.partnerMember, ...(jointAccountHolders ?? [])].filter((v) => !!v),
        'id',
    );

    const onPressNext = (updates?: Partial<UpdateBankAccountInfo>) => {
        if (updates != null) {
            setUpdateAccountInfo({
                ...updateAccountInfo,
                ...updates,
            });
        }
        setIndex(index + 1);
    };

    const navigateBack = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.navigate('/home/banking');
        }
    };

    const onPressBack = () => {
        if (index > 0) {
            setIndex(index - 1);
        } else {
            // Clear out account being edited
            setAccount(null);
            navigateBack();
        }
    };

    const onSubmit = async ({
        nickname,
        icon,
        color,
        jointEmail,
    }: UpdateBankAccountInfo) => {
        if (isEditMode) {
            // Update account: we require either a joint user is invited or the metadata has been updated
            if (
                nickname !== account?.nickname ||
                icon !== account?.icon ||
                color !== account?.color
            ) {
                try {
                    await updateAccount({
                        urlParams: { bankAccountId: account.bankAccountId },
                        body: {
                            syncteraAccountId: account.syncteraAccountId,
                            nickname,
                            icon,
                            color,
                        },
                    });
                    router.replace('/home/banking');
                    toast.show('Account updated!');
                    setAccount(null); // Clear account being edited from global state
                } catch (e) {
                    captureException(e);
                    toast.show('Something went wrong updating your account');
                }
            } else if (isEmpty(jointEmail)) {
                // If nothing is updated, then take user back a page
                setAccount(null); // Clear account being edited from global state
                router.back();
            }

            if (jointEmail != null) {
                try {
                    await sendJointInvite({
                        body: {
                            fromUserId: user.id,
                            toEmail: jointEmail,
                            bankAccountId: account.bankAccountId,
                        },
                    });
                    router.replace('/home/banking');
                    toast.show('Joint account holder invited!');
                    setAccount(null); // Clear account being edited from global state
                } catch (e) {
                    captureException(e);
                    toast.show(
                        'Something went wrong inviting your joint account holder.',
                    );
                }
            }
        } else {
            // Creating new savings space: validate we have all the metadata needed
            if (isEmpty(nickname) || isEmpty(icon) || isEmpty(color)) {
                toast.show(
                    'Please select an icon and color, and enter a nickname before creating your savings space',
                );
                return;
            }

            try {
                const createdAccount = await createSavingsSpace({
                    body: {
                        userId: user.id,
                        accountType: AccountType.SAVING,
                        nickname: nickname as string,
                        icon: icon as string,
                        color: color as string,
                        ...(!isEmpty(jointEmail) && { jointEmail }),
                        disclosures: getBankingDisclosures({
                            policyTypes: HYS_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                        }),
                    },
                });

                setPreselectedTransferAccount({
                    toSyncteraAccountId: createdAccount.syncteraAccountId,
                });

                void refetchAccountDetails();
                router.replace('/banking/transfer');
                toast.show('Savings Space created!', { duration: 3000 });
            } catch (e) {
                captureException(e);
                if (getErrorMessage(e)?.includes('max account limit')) {
                    router.replace('/home/banking');
                    toast.show("You've reached your maximum account limit");
                } else {
                    toast.show(
                        'Something went wrong creating your Savings Space',
                    );
                }
            }
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: CreateEditSteps } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const Content = (() => {
            switch (route.key) {
                case CreateEditSteps.NAME:
                    return (
                        <Name
                            onPressNext={onPressNext}
                            accountInfo={updateAccountInfo}
                        />
                    );
                case CreateEditSteps.ICON:
                    return (
                        <Icon
                            onPressNext={onPressNext}
                            accountInfo={updateAccountInfo}
                        />
                    );
                case CreateEditSteps.COLOR:
                    return (
                        <Color
                            onPressNext={onPressNext}
                            accountInfo={updateAccountInfo}
                        />
                    );
                case CreateEditSteps.OWNERSHIP:
                    return (
                        <Ownership
                            isEditMode={isEditMode}
                            hideDisclosures={hideDisclosures}
                            allowJointInvite={allowJointInvite}
                            accountInfo={updateAccountInfo}
                            associatedUsers={associatedUsers}
                            onPressSubmit={(updates) => {
                                void onSubmit({
                                    ...updateAccountInfo,
                                    ...updates,
                                });
                            }}
                        />
                    );
                default:
                    return <></>;
            }
        })();

        return (
            <ScrollView
                flex={1}
                contentContainerStyle={{ flexGrow: 1 }}
                paddingTop={12}
                paddingHorizontal={16}
                maxWidth={500}
                $sm={{ marginTop: 50, alignSelf: 'center' }}
                $platform-web={{ maxHeight: 650, paddingBottom: 20 }}
            >
                {Content}
            </ScrollView>
        );
    };

    useEffect(() => {
        const { allowed } = canUserCreateBankAccount(user);
        if (
            isCreateMode &&
            (!allowed ||
                accessToRestrictedCapabilities.openBankAccount === false)
        ) {
            navigateBack();
            toast.show("Sorry, but you can't create a new account right now.");
        }
    }, [accessToRestrictedCapabilities.openBankAccount]);

    const isLoading =
        isCreateSavingsSpacePending ||
        isUpdateAccountPending ||
        isSendJointInvitePending;

    if (isLoading || accessToRestrictedCapabilities.openBankAccount === null) {
        return <FullScreenSpinner />;
    }

    return (
        <Pressable
            onPress={Keyboard.dismiss}
            style={{ flex: 1 }}
            disabled={isWeb}
        >
            <SafeAreaView style={{ flex: 1 }}>
                <YStack flex={1}>
                    <TabView
                        swipeEnabled={false}
                        navigationState={{ index, routes }}
                        onIndexChange={setIndex}
                        renderScene={renderScene}
                        renderTabBar={() => (
                            <View paddingVertical={12}>
                                <BodyMediumRegular textAlign="center">
                                    {isEditMode
                                        ? 'Editing a space'
                                        : 'Creating a space'}
                                </BodyMediumRegular>
                                <View alignSelf="flex-start" marginTop={-28}>
                                    <BackButton
                                        fallbackRoute="/home/banking"
                                        customBackFn={onPressBack}
                                    />
                                </View>
                            </View>
                        )}
                    />
                </YStack>
            </SafeAreaView>
        </Pressable>
    );
};

export default CreateEditSpace;
